import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Turnstile } from '@marsidev/react-turnstile';
import LegalLayout, { LegalSection, LegalDisclaimer, LegalLastUpdated } from '../LegalLayout';
import { APP_NAME } from '../../config/branding';
import {
  CONTACT_MAX_FILE_BYTES,
  CONTACT_CATEGORIES,
  DEFAULT_CONTACT_CATEGORY,
  resolveContactCategory,
  SUPPORT_EMAIL,
} from '../legalConstants';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_CLOUDFARE_TURNSTILE_CLIENT_SITE_KEY || '';
const MAX_FILE_MB = (CONTACT_MAX_FILE_BYTES / (1024 * 1024)).toFixed(1);

const ALLOWED_EXTENSIONS = [
  '.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp',
  '.pdf',
  '.mp4', '.mov', '.webm', '.avi', '.mkv',
];

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [category, setCategory] = useState(DEFAULT_CONTACT_CATEGORY);
  const [message, setMessage] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const turnstileRef = useRef(null);
  const fileInputRef = useRef(null);

  const turnstileRequired = Boolean(TURNSTILE_SITE_KEY);

  useEffect(() => {
    setCategory(resolveContactCategory(searchParams.get('category')));
  }, [searchParams]);

  const resetTurnstile = useCallback(() => {
    setTurnstileToken('');
    turnstileRef.current?.reset();
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setStatus(null);
    if (!file) {
      setAttachment(null);
      return;
    }
    if (file.size > CONTACT_MAX_FILE_BYTES) {
      setAttachment(null);
      setStatus({ type: 'error', text: `File must be ${MAX_FILE_MB} MB or smaller (selected: ${formatBytes(file.size)}).` });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    const ext = `.${file.name.split('.').pop()?.toLowerCase() || ''}`;
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      setAttachment(null);
      setStatus({
        type: 'error',
        text: 'File type not allowed. Supported: images, PDF, and common video formats.',
      });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setAttachment(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);

    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedMessage = message.trim();
    const resolvedCategory = resolveContactCategory(category);

    if (!trimmedName) {
      setStatus({ type: 'error', text: 'Name is required.' });
      return;
    }
    if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setStatus({ type: 'error', text: 'A valid email address is required.' });
      return;
    }
    if (!trimmedMessage) {
      setStatus({ type: 'error', text: 'Please enter a message.' });
      return;
    }
    if (turnstileRequired && !turnstileToken) {
      setStatus({ type: 'error', text: 'Please complete the human verification check.' });
      return;
    }

    setBusy(true);
    try {
      const formData = new FormData();
      formData.append('name', trimmedName);
      formData.append('email', trimmedEmail);
      formData.append('message', trimmedMessage);
      formData.append('category', resolvedCategory);
      if (phone.trim()) formData.append('phone', phone.trim());
      if (attachment) formData.append('attachment', attachment);
      if (turnstileToken) formData.append('turnstileToken', turnstileToken);

      const apiUrl = `${(process.env.REACT_APP_BASE_URL || '').replace(/\/$/, '')}/api/public/contact`;

      const response = await axios.post(apiUrl, formData, {
        withCredentials: false,
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data?.success) {
        setStatus({ type: 'success', text: response.data.message || 'Message sent. Thank you.' });
        setName('');
        setEmail('');
        setPhone('');
        setMessage('');
        setCategory(DEFAULT_CONTACT_CATEGORY);
        setAttachment(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        resetTurnstile();
      } else {
        setStatus({ type: 'error', text: response.data?.message || 'Unable to send message.' });
        resetTurnstile();
      }
    } catch (err) {
      setStatus({
        type: 'error',
        text: err.response?.data?.message || 'Unable to send message right now. Please try again or email us directly.',
      });
      resetTurnstile();
    } finally {
      setBusy(false);
    }
  };

  const submitDisabled = busy || (turnstileRequired && !turnstileToken);

  return (
    <LegalLayout
      title="Contact Us"
      subtitle={`Reach the ${APP_NAME} team — questions, feedback, legal notices, and support.`}
    >
      <LegalLastUpdated />

      <LegalSection title="Contact form">
        <p>
          Submit the form below and we will respond as soon as we can. No sign-in is required.
          Human verification (Cloudflare Turnstile) helps us reduce automated spam.
        </p>

        <form className="legal-contact-form" onSubmit={handleSubmit} noValidate>
          <div className="legal-field">
            <label htmlFor="contact-category">Category *</label>
            <select
              id="contact-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              required
              disabled={busy}
            >
              {CONTACT_CATEGORIES.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>

          <div className="legal-field">
            <label htmlFor="contact-name">Name *</label>
            <input
              id="contact-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoComplete="name"
              disabled={busy}
            />
          </div>

          <div className="legal-field">
            <label htmlFor="contact-email">Email *</label>
            <input
              id="contact-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              disabled={busy}
            />
          </div>

          <div className="legal-field">
            <label htmlFor="contact-phone">
              Phone <span className="legal-optional">(optional)</span>
            </label>
            <input
              id="contact-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              autoComplete="tel"
              disabled={busy}
            />
          </div>

          <div className="legal-field">
            <label htmlFor="contact-message">Message *</label>
            <textarea
              id="contact-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              disabled={busy}
            />
          </div>

          <div className="legal-field">
            <label htmlFor="contact-attachment">
              Attachment <span className="legal-optional">(optional)</span>
            </label>
            <input
              id="contact-attachment"
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*,.pdf"
              onChange={handleFileChange}
              disabled={busy}
            />
            <span className="legal-field-hint">
              Images, PDF, or video — max {MAX_FILE_MB} MB.{' '}
              {attachment ? `Selected: ${attachment.name} (${formatBytes(attachment.size)})` : ''}
            </span>
          </div>

          {turnstileRequired ? (
            <div className="legal-turnstile">
              <Turnstile
                ref={turnstileRef}
                siteKey={TURNSTILE_SITE_KEY}
                onSuccess={setTurnstileToken}
                onExpire={() => setTurnstileToken('')}
                onError={() => setTurnstileToken('')}
              />
            </div>
          ) : null}

          <button type="submit" className="legal-submit" disabled={submitDisabled}>
            {busy ? 'Sending…' : 'Send message'}
          </button>

          {status ? (
            <div className={`legal-form-status legal-form-status--${status.type}`} role="alert">
              {status.text}
            </div>
          ) : null}
        </form>
      </LegalSection>

      <LegalSection title="What to use this form for">
        <ul>
          {CONTACT_CATEGORIES.map((item) => (
            <li key={item.value}>{item.label}</li>
          ))}
        </ul>
      </LegalSection>

      <LegalSection title="Email">
        <p>
          For email inquiries:{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="legal-inline-link">{SUPPORT_EMAIL}</a>
        </p>
      </LegalSection>

      <LegalDisclaimer />
    </LegalLayout>
  );
}
